export const operatorMessages = {
  en: {
    operator: {
      appTitle: 'Machine Operator',
      appDescription: 'Machine status, assigned orders, setup, counters, faults, downtime, maintenance, and efficiency.',
      nav: {
        machines: 'Machines',
        efficiency: 'Efficiency',
      },
      machines: {
        eyebrow: 'Machine park',
        title: 'Machines',
        description: 'Current status of all production machines.',
        status: 'Status',
        allStatuses: 'All statuses',
        running: 'Running',
        idle: 'Idle',
        down: 'Down',
        setup: 'Setup',
        machine: 'Machine',
        order: 'Order',
        lot: 'Lot',
        counter: 'Shift counter',
        fault: 'Fault',
        noFault: 'No fault',
        activeFault: 'Fault active',
        actions: 'Actions',
        open: 'Open',
        loading: 'Loading machines…',
        empty: 'No machines found.',
        error: 'Failed to load machines.',
        retry: 'Retry',
        total: '{count} machines',
        runningCount: '{count} running',
        downCount: '{count} down',
      },
      detail: {
        eyebrow: 'Machine',
        back: 'Back to machines',
        title: 'Machine detail',
        status: 'Status',
        running: 'Running',
        idle: 'Idle',
        down: 'Down',
        setup: 'Setup',
        currentOrder: 'Assigned order',
        noOrder: 'No production order assigned.',
        currentLot: 'Current lot',
        counterTotal: 'Total counter',
        counterShift: 'Shift counter',
        assignments: 'Assignments',
        noAssignments: 'No active assignments for this machine.',
        employee: 'Employee',
        stage: 'Stage',
        shift: 'Shift',
        active: 'Active',
        ended: 'Ended',
        setupParameters: 'Setup parameters',
        setupPlaceholder: 'Setup snapshot endpoint is not yet available. Configured parameters will appear here.',
        materialRequirements: 'Yarn & material requirements',
        materialPlaceholder: 'Material requirements endpoint is not yet available.',
        downtime: 'Downtime',
        reportDowntime: 'Report downtime',
        selectReason: 'Select reason',
        report: 'Report',
        reporting: 'Reporting…',
        close: 'Close',
        closeConfirm: 'Close this downtime event?',
        noDowntime: 'No downtime events for this machine.',
        openStatus: 'Open',
        closedStatus: 'Closed',
        resolvedStatus: 'Resolved',
        openItems: '{count} open',
        closedItems: '{count} closed',
        resolvedItems: '{count} resolved',
        doneItems: '{count} done',
        faults: 'Faults',
        reportFault: 'Report fault',
        faultPlaceholder: 'Fault logging endpoint is not yet available.',
        maintenance: 'Maintenance',
        requestMaintenance: 'Request maintenance',
        maintenancePlaceholder: 'Maintenance request endpoint is not yet available.',
        loading: 'Loading machine…',
        error: 'Failed to load machine.',
        retry: 'Retry',
        opened: 'Downtime reported.',
        closed: 'Downtime closed.',
        noSetup: 'No setup snapshot saved for this machine.',
        saveSetup: 'Save setup',
        setupValue: 'Value',
        setupContext: 'Setup context',
        setupReadiness: 'Setup readiness',
        setupReadinessValue: '{count}/{total} checked',
        setupRequired: 'Complete every setup parameter before saving.',
        setupNeedsLot: 'Assign a lot before saving the setup snapshot.',
        setupNotesPlaceholder: 'Optional setup note',
        saving: 'Saving…',
        saved: 'Saved.',
        save: 'Save',
        noRequirements: 'No material requirements for the current order.',
        perPair: 'per pair',
        requiredQuantity: 'Required',
        refreshMaterials: 'Refresh',
        materialContext: 'Material context',
        plannedPairs: 'Planned pairs',
        totalRequired: 'Total required',
        material: 'Material',
        materialsLoading: 'Loading material requirements…',
        noStageRequirements: 'No material requirements for the assigned stage.',
        materialReadiness: 'Material readiness',
        materialReadinessValue: '{count} rows',
        noFaults: 'No faults reported.',
        faultCode: 'Fault code',
        description: 'Description',
        resolve: 'Resolve',
        resolveConfirm: 'Resolve this fault?',
        resolved: 'Fault resolved.',
        faultReported: 'Fault reported.',
        reportFaultPlaceholder: 'e.g. Needle 42 broken',
        noMaintenance: 'No maintenance requests.',
        reason: 'Reason',
        priority: 'Priority',
        priorityLow: 'Low',
        priorityMedium: 'Medium',
        priorityHigh: 'High',
        requested: 'Requested.',
        advance: 'Advance status',
        statusInProgress: 'In progress',
        statusDone: 'Done',
        recordCounter: 'Record counter',
        counterTotalLabel: 'Total counter',
        counterShiftLabel: 'Shift counter',
        counterRecorded: 'Counter recorded.',
        recordingCounter: 'Recording…',
        counterCannotDecrease: 'Counter values cannot be lower than the current readings.',
        counterHint: 'Current total {total}, shift {shift}',
        counterDelta: 'New output: total +{total}, shift +{shift}',
        cancel: 'Cancel',
        notes: 'Notes',
        workflow: 'Operator workflow',
        orderProgress: 'Order progress',
        planned: 'Planned',
        produced: 'Produced',
        remaining: 'Remaining',
        assignedStage: 'Assigned stage',
        assignedLot: 'Assigned lot',
        assignedOperator: 'Assigned operator',
        machineReadiness: 'Machine readiness',
        workflowStates: {
          noOrder: 'No order',
          missingLot: 'Lot required',
          blocked: 'Blocked',
          setup: 'Setup',
          ready: 'Ready',
        },
        workflowHints: {
          noOrder: 'Assign a production order before starting operator work on this machine.',
          missingLot: 'Choose or assign the active lot before recording counters, downtime, or setup checks.',
          blocked: 'Resolve the active fault or downtime state before continuing production on this machine.',
          setup: 'Verify setup parameters, yarn feed, and assigned operator before switching to production.',
          ready: 'The machine has an order and lot context. Continue with counters, downtime, setup, or material checks as needed.',
        },
        setupLabels: {
          needle_count: 'Needle count',
          gauge: 'Gauge',
          tension: 'Tension',
          yarn_feed: 'Yarn feed',
        },
      },
      efficiency: {
        eyebrow: 'Performance',
        title: 'Machine efficiency',
        description: 'Output efficiency by stage for the selected date.',
        date: 'Date',
        stage: 'Stage',
        completed: 'Completed',
        rejected: 'Rejected',
        rework: 'Rework',
        efficiency: 'Efficiency',
        efficiencyValue: '{percent}%',
        noData: 'No output data for this date.',
        loading: 'Loading efficiency…',
        error: 'Failed to load efficiency.',
        retry: 'Retry',
        totalCompleted: 'Total completed',
        totalRejected: 'Total rejected',
      },
      state: {
        loading: 'Loading…',
        error: 'Something went wrong.',
        retry: 'Retry',
        noData: 'No data available.',
      },
    },
  },
  uz: {
    operator: {
      appTitle: 'Mashina operatori',
      appDescription: 'Mashina holati, tayinlangan buyurtmalar, sozlamalar, hisoblagichlar, nosozliklar, to‘xtalishlar, ta’mirlash va samaradorlik.',
      nav: {
        machines: 'Mashinalar',
        efficiency: 'Samaradorlik',
      },
      machines: {
        eyebrow: 'Mashina parki',
        title: 'Mashinalar',
        description: 'Barcha ishlab chiqarish mashinalarining joriy holati.',
        status: 'Holat',
        allStatuses: 'Barcha holatlar',
        running: 'Ishlamoqda',
        idle: 'Bo‘sh',
        down: 'To‘xtagan',
        setup: 'Sozlashda',
        machine: 'Mashina',
        order: 'Buyurtma',
        lot: 'Partiya',
        counter: 'Smena hisoblagichi',
        fault: 'Nosozlik',
        noFault: 'Nosozlik yo‘q',
        activeFault: 'Faol nosozlik',
        actions: 'Amallar',
        open: 'Ochish',
        loading: 'Mashinalar yuklanmoqda…',
        empty: 'Mashinalar topilmadi.',
        error: 'Mashinalarni yuklab bo‘lmadi.',
        retry: 'Qayta urinish',
        total: '{count} ta mashina',
        runningCount: '{count} ta ishlamoqda',
        downCount: '{count} ta to‘xtagan',
      },
      detail: {
        eyebrow: 'Mashina',
        back: 'Mashinalarga qaytish',
        title: 'Mashina tafsiloti',
        status: 'Holat',
        running: 'Ishlamoqda',
        idle: 'Bo‘sh',
        down: 'To‘xtagan',
        setup: 'Sozlashda',
        currentOrder: 'Tayinlangan buyurtma',
        noOrder: 'Ishlab chiqarish buyurtmasi tayinlanmagan.',
        currentLot: 'Joriy partiya',
        counterTotal: 'Umumiy hisoblagich',
        counterShift: 'Smena hisoblagichi',
        assignments: 'Taqsimotlar',
        noAssignments: 'Ushbu mashina uchun faol taqsimotlar mavjud emas.',
        employee: 'Xodim',
        stage: 'Bosqich',
        shift: 'Smena',
        active: 'Faol',
        ended: 'Yakunlangan',
        setupParameters: 'Sozlash parametrlari',
        setupPlaceholder: 'Sozlash snapshoti uchun endpoint hali mavjud emas. Sozlangan parametrlar shu yerda ko‘rinadi.',
        materialRequirements: 'Iplar va material talablari',
        materialPlaceholder: 'Material talablari uchun endpoint hali mavjud emas.',
        downtime: 'To‘xtalishlar',
        reportDowntime: 'To‘xtalish haqida xabar berish',
        selectReason: 'Sababni tanlang',
        report: 'Xabar berish',
        reporting: 'Yuborilmoqda…',
        close: 'Yopish',
        closeConfirm: 'Ushbu to‘xtalish hodisasini yopish kerakmi?',
        noDowntime: 'Ushbu mashina uchun to‘xtalish hodisalari mavjud emas.',
        openStatus: 'Ochiq',
        closedStatus: 'Yopilgan',
        resolvedStatus: 'Hal qilingan',
        openItems: '{count} ochiq',
        closedItems: '{count} yopilgan',
        resolvedItems: '{count} hal qilingan',
        doneItems: '{count} bajarilgan',
        faults: 'Nosozliklar',
        reportFault: 'Nosozlik haqida xabar berish',
        faultPlaceholder: 'Nosozliklarni qayd etish uchun endpoint hali mavjud emas.',
        maintenance: 'Ta’mirlash',
        requestMaintenance: 'Ta’mirlash so‘rovi',
        maintenancePlaceholder: 'Ta’mirlash so‘rovi uchun endpoint hali mavjud emas.',
        loading: 'Mashina yuklanmoqda…',
        error: 'Mashinani yuklab bo‘lmadi.',
        retry: 'Qayta urinish',
        opened: 'To‘xtalish qayd etildi.',
        closed: 'To‘xtalish yopildi.',
        noSetup: 'Ushbu mashina uchun sozlash snapshoti saqlanmagan.',
        saveSetup: 'Sozlashni saqlash',
        setupValue: 'Qiymat',
        setupContext: 'Sozlash konteksti',
        setupReadiness: 'Sozlash tayyorligi',
        setupReadinessValue: '{count}/{total} tekshirildi',
        setupRequired: 'Saqlashdan oldin barcha sozlash parametrlarini toldiring.',
        setupNeedsLot: 'Sozlash snapshotini saqlashdan oldin partiyani tayinlang.',
        setupNotesPlaceholder: 'Ixtiyoriy sozlash izohi',
        saving: 'Saqlanmoqda…',
        saved: 'Saqlandi.',
        save: 'Saqlash',
        noRequirements: 'Joriy buyurtma uchun material talablari mavjud emas.',
        perPair: 'jufti uchun',
        requiredQuantity: 'Zarur miqdor',
        refreshMaterials: 'Yangilash',
        materialContext: 'Material konteksti',
        plannedPairs: 'Rejadagi juft',
        totalRequired: 'Jami zarur',
        material: 'Material',
        materialsLoading: 'Material talablari yuklanmoqda…',
        noStageRequirements: 'Tayinlangan bosqich uchun material talablari mavjud emas.',
        materialReadiness: 'Material tayyorligi',
        materialReadinessValue: '{count} qator',
        noFaults: 'Nosozliklar qayd etilmagan.',
        faultCode: 'Nosozlik kodi',
        description: 'Tavsif',
        resolve: 'Hal qilish',
        resolveConfirm: 'Ushbu nosozlikni hal qilish kerakmi?',
        resolved: 'Nosozlik hal qilindi.',
        faultReported: 'Nosozlik qayd etildi.',
        reportFaultPlaceholder: 'Masalan: 42-igna sinib ketdi',
        noMaintenance: 'Ta’mirlash so‘rovlari mavjud emas.',
        reason: 'Sabab',
        priority: 'Ustuvorlik',
        priorityLow: 'Past',
        priorityMedium: 'O‘rta',
        priorityHigh: 'Yuqori',
        requested: 'So‘rov yuborildi.',
        advance: 'Holatni o‘zgartirish',
        statusInProgress: 'Jarayonda',
        statusDone: 'Bajarildi',
        recordCounter: 'Hisoblagichni qayd etish',
        counterTotalLabel: 'Umumiy hisoblagich',
        counterShiftLabel: 'Smena hisoblagichi',
        counterRecorded: 'Hisoblagich qayd etildi.',
        recordingCounter: 'Qayd etilmoqda…',
        counterCannotDecrease: 'Hisoblagich qiymatlari joriy korsatkichlardan kichik bolmasligi kerak.',
        counterHint: 'Joriy umumiy {total}, smena {shift}',
        counterDelta: 'Yangi ishlab chiqarish: umumiy +{total}, smena +{shift}',
        cancel: 'Bekor qilish',
        notes: 'Izohlar',
        workflow: 'Operator jarayoni',
        orderProgress: 'Buyurtma bajarilishi',
        planned: 'Reja',
        produced: 'Ishlab chiqarilgan',
        remaining: 'Qolgan',
        assignedStage: 'Tayinlangan bosqich',
        assignedLot: 'Tayinlangan partiya',
        assignedOperator: 'Tayinlangan operator',
        machineReadiness: 'Mashina tayyorligi',
        workflowStates: {
          noOrder: 'Buyurtma yoq',
          missingLot: 'Partiya kerak',
          blocked: 'Bloklangan',
          setup: 'Sozlash',
          ready: 'Tayyor',
        },
        workflowHints: {
          noOrder: 'Bu mashinada ish boshlashdan oldin ishlab chiqarish buyurtmasini tayinlang.',
          missingLot: 'Hisoblagich, toxtalish yoki sozlashni qayd etishdan oldin faol partiyani tanlang.',
          blocked: 'Ishlab chiqarishni davom ettirishdan oldin faol nosozlik yoki toxtalish holatini hal qiling.',
          setup: 'Ishlab chiqarishga otishdan oldin sozlash parametrlari, ip uzatish va operatorni tekshiring.',
          ready: 'Mashinada buyurtma va partiya konteksti bor. Hisoblagich, toxtalish, sozlash yoki material tekshiruvlarini davom ettiring.',
        },
        setupLabels: {
          needle_count: 'Ignalar soni',
          gauge: 'Gauge',
          tension: 'Taranglik',
          yarn_feed: 'Ip uzatish',
        },
      },
      efficiency: {
        eyebrow: 'Ko‘rsatkichlar',
        title: 'Mashina samaradorligi',
        description: 'Tanlangan sana uchun bosqichlar kesimida ishlab chiqarish samaradorligi.',
        date: 'Sana',
        stage: 'Bosqich',
        completed: 'Bajarilgan',
        rejected: 'Rad etilgan',
        rework: 'Qayta ishlash',
        efficiency: 'Samaradorlik',
        efficiencyValue: '{percent}%',
        noData: 'Ushbu sana uchun ishlab chiqarish ma’lumotlari mavjud emas.',
        loading: 'Samaradorlik yuklanmoqda…',
        error: 'Samaradorlikni yuklab bo‘lmadi.',
        retry: 'Qayta urinish',
        totalCompleted: 'Jami bajarilgan',
        totalRejected: 'Jami rad etilgan',
      },
      state: {
        loading: 'Yuklanmoqda…',
        error: 'Xatolik yuz berdi.',
        retry: 'Qayta urinish',
        noData: 'Ma’lumot mavjud emas.',
      },
    },
  },
  ru: {
    operator: {
      appTitle: 'Оператор оборудования',
      appDescription: 'Состояние оборудования, назначенные заказы, настройки, счётчики, неисправности, простои, обслуживание и эффективность.',
      nav: {
        machines: 'Оборудование',
        efficiency: 'Эффективность',
      },
      machines: {
        eyebrow: 'Парк оборудования',
        title: 'Оборудование',
        description: 'Текущее состояние всего производственного оборудования.',
        status: 'Статус',
        allStatuses: 'Все статусы',
        running: 'Работает',
        idle: 'Простаивает',
        down: 'Остановлено',
        setup: 'Настройка',
        machine: 'Машина',
        order: 'Заказ',
        lot: 'Партия',
        counter: 'Счётчик смены',
        fault: 'Неисправность',
        noFault: 'Нет неисправностей',
        activeFault: 'Активная неисправность',
        actions: 'Действия',
        open: 'Открыть',
        loading: 'Загрузка оборудования…',
        empty: 'Оборудование не найдено.',
        error: 'Не удалось загрузить оборудование.',
        retry: 'Повторить',
        total: '{count} машин',
        runningCount: '{count} работает',
        downCount: '{count} остановлено',
      },
      detail: {
        eyebrow: 'Машина',
        back: 'К оборудованию',
        title: 'Детали машины',
        status: 'Статус',
        running: 'Работает',
        idle: 'Простаивает',
        down: 'Остановлено',
        setup: 'Настройка',
        currentOrder: 'Назначенный заказ',
        noOrder: 'Производственный заказ не назначен.',
        currentLot: 'Текущая партия',
        counterTotal: 'Общий счётчик',
        counterShift: 'Счётчик смены',
        assignments: 'Назначения',
        noAssignments: 'Нет активных назначений для этой машины.',
        employee: 'Сотрудник',
        stage: 'Этап',
        shift: 'Смена',
        active: 'Активен',
        ended: 'Завершено',
        setupParameters: 'Параметры настройки',
        setupPlaceholder: 'Эндпоинт снимка настроек пока недоступен. Настроенные параметры появятся здесь.',
        materialRequirements: 'Требования к пряже и материалам',
        materialPlaceholder: 'Эндпоинт требований к материалам пока недоступен.',
        downtime: 'Простои',
        reportDowntime: 'Сообщить о простое',
        selectReason: 'Выберите причину',
        report: 'Сообщить',
        reporting: 'Отправка…',
        close: 'Закрыть',
        closeConfirm: 'Закрыть этот простой?',
        noDowntime: 'Нет простоев для этой машины.',
        openStatus: 'Открыто',
        closedStatus: 'Закрыто',
        resolvedStatus: 'Решено',
        openItems: '{count} открыто',
        closedItems: '{count} закрыто',
        resolvedItems: '{count} решено',
        doneItems: '{count} готово',
        faults: 'Неисправности',
        reportFault: 'Сообщить о неисправности',
        faultPlaceholder: 'Эндпоинт регистрации неисправностей пока недоступен.',
        maintenance: 'Обслуживание',
        requestMaintenance: 'Запросить обслуживание',
        maintenancePlaceholder: 'Эндпоинт запросов на обслуживание пока недоступен.',
        loading: 'Загрузка машины…',
        error: 'Не удалось загрузить машину.',
        retry: 'Повторить',
        opened: 'Простой зарегистрирован.',
        closed: 'Простой закрыт.',
        noSetup: 'Снимок настроек для этой машины не сохранён.',
        saveSetup: 'Сохранить настройки',
        setupValue: 'Значение',
        setupContext: 'Контекст настройки',
        setupReadiness: 'Готовность настройки',
        setupReadinessValue: '{count}/{total} проверено',
        setupRequired: 'Заполните все параметры настройки перед сохранением.',
        setupNeedsLot: 'Назначьте партию перед сохранением снимка настройки.',
        setupNotesPlaceholder: 'Необязательное примечание к настройке',
        saving: 'Сохранение…',
        saved: 'Сохранено.',
        save: 'Сохранить',
        noRequirements: 'Нет требований к материалам для текущего заказа.',
        perPair: 'за пару',
        requiredQuantity: 'Требуется',
        refreshMaterials: 'Обновить',
        materialContext: 'Контекст материалов',
        plannedPairs: 'План пар',
        totalRequired: 'Всего требуется',
        material: 'Материал',
        materialsLoading: 'Загрузка требований к материалам…',
        noStageRequirements: 'Нет требований к материалам для назначенного этапа.',
        materialReadiness: 'Готовность материалов',
        materialReadinessValue: '{count} строк',
        noFaults: 'Неисправности не зарегистрированы.',
        faultCode: 'Код неисправности',
        description: 'Описание',
        resolve: 'Решить',
        resolveConfirm: 'Решить эту неисправность?',
        resolved: 'Неисправность решена.',
        faultReported: 'Неисправность зарегистрирована.',
        reportFaultPlaceholder: 'напр. Сломалась игла 42',
        noMaintenance: 'Нет запросов на обслуживание.',
        reason: 'Причина',
        priority: 'Приоритет',
        priorityLow: 'Низкий',
        priorityMedium: 'Средний',
        priorityHigh: 'Высокий',
        requested: 'Запрос отправлен.',
        advance: 'Изменить статус',
        statusInProgress: 'В работе',
        statusDone: 'Готово',
        recordCounter: 'Записать счётчик',
        counterTotalLabel: 'Общий счётчик',
        counterShiftLabel: 'Счётчик смены',
        counterRecorded: 'Счётчик записан.',
        recordingCounter: 'Запись…',
        counterCannotDecrease: 'Значения счётчика не могут быть ниже текущих показаний.',
        counterHint: 'Текущий общий {total}, смена {shift}',
        counterDelta: 'Новая выработка: общий +{total}, смена +{shift}',
        cancel: 'Отмена',
        notes: 'Примечания',
        workflow: 'Рабочий процесс оператора',
        orderProgress: 'Выполнение заказа',
        planned: 'План',
        produced: 'Произведено',
        remaining: 'Осталось',
        assignedStage: 'Назначенный этап',
        assignedLot: 'Назначенная партия',
        assignedOperator: 'Назначенный оператор',
        machineReadiness: 'Готовность машины',
        workflowStates: {
          noOrder: 'Нет заказа',
          missingLot: 'Нужна партия',
          blocked: 'Заблокировано',
          setup: 'Настройка',
          ready: 'Готово',
        },
        workflowHints: {
          noOrder: 'Назначьте производственный заказ перед началом работы на этой машине.',
          missingLot: 'Выберите или назначьте активную партию перед записью счетчиков, простоев или проверок настройки.',
          blocked: 'Устраните активную неисправность или простой перед продолжением производства на этой машине.',
          setup: 'Проверьте параметры настройки, подачу пряжи и назначенного оператора перед переходом к производству.',
          ready: 'У машины есть контекст заказа и партии. Продолжайте со счетчиками, простоями, настройкой или проверками материалов.',
        },
        setupLabels: {
          needle_count: 'Количество игл',
          gauge: 'Класс (E)',
          tension: 'Натяжение',
          yarn_feed: 'Подача пряжи',
        },
      },
      efficiency: {
        eyebrow: 'Показатели',
        title: 'Эффективность оборудования',
        description: 'Эффективность выработки по этапам за выбранную дату.',
        date: 'Дата',
        stage: 'Этап',
        completed: 'Выполнено',
        rejected: 'Брак',
        rework: 'Переработка',
        efficiency: 'Эффективность',
        efficiencyValue: '{percent}%',
        noData: 'Нет данных о выработке за эту дату.',
        loading: 'Загрузка эффективности…',
        error: 'Не удалось загрузить эффективность.',
        retry: 'Повторить',
        totalCompleted: 'Всего выполнено',
        totalRejected: 'Всего брака',
      },
      state: {
        loading: 'Загрузка…',
        error: 'Что-то пошло не так.',
        retry: 'Повторить',
        noData: 'Нет данных.',
      },
    },
  },
}
